import { Children, useState, useEffect, useRef } from 'react';
import { ControlButton, ProgressDots } from '@care/react-component-lib';
import { Icon24ChevronLeft, Icon24ChevronRight } from '@care/react-embrace-icons';
import { Box, useTheme } from '@mui/material';
import { Icon24UtilityChevronSmallLeft, Icon24UtilityChevronSmallRight } from '@care/react-icons';
import { trackAmplitudeEvent, AmplitudeEvent } from '@/lib/analyticsHelper';

export interface CarouselProps {
  children: React.ReactNode;
  controlVariant?: 'sides' | 'topRight' | 'none';
  showProgressIndicator?: boolean;
  itemGap?: number | string;
  itemsPerView?: number;
  slotProps?: { 'data-testid'?: string };
}

export const Carousel: React.FC<CarouselProps> = ({
  children,
  controlVariant = 'none',
  showProgressIndicator = false,
  itemGap = 3,
  itemsPerView = 3,
  slotProps,
}: CarouselProps) => {
  const theme = useTheme();
  const originalSlides = Children.toArray(children);
  const totalSteps = originalSlides.length;
  const slides = [...originalSlides, ...originalSlides, ...originalSlides];
  const gapInPixels = parseInt(theme.spacing(itemGap), 10);

  const [currentSlide, setCurrentSlide] = useState(totalSteps);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideOffset, setSlideOffset] = useState(0);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const resizeObserver = new ResizeObserver((entries) => {
      const containerWidth = entries[0].contentRect.width;
      const itemWidth = (containerWidth - (itemsPerView - 1) * gapInPixels) / itemsPerView;
      containerRef.current?.style.setProperty('--item-width', `${itemWidth}px`);
      setSlideOffset(itemWidth + gapInPixels);
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [itemsPerView, gapInPixels]);

  useEffect(() => {
    if (!isTransitioning) return undefined;
    const timer = setTimeout(() => {
      setIsTransitioning(false);

      // When we reach the third set, jump back to equivalent position in second set
      if (currentSlide >= totalSteps * 2) {
        setCurrentSlide(currentSlide - totalSteps);
      }

      // When we reach the first set, jump to equivalent position in second set
      if (currentSlide < totalSteps) {
        setCurrentSlide(currentSlide + totalSteps);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [currentSlide, isTransitioning, totalSteps]);

  const amplitudeEventButtonClick = () => {
    trackAmplitudeEvent(AmplitudeEvent.seoFeatureInteracted, {
      feature_name: 'Rotate Article Carousel',
    });
  };

  const goToNextSlide = () => {
    if (isTransitioning) {
      return;
    }
    setIsTransitioning(true);
    amplitudeEventButtonClick();
    setCurrentSlide((curr) => {
      const newSlide = curr + 1;
      return newSlide;
    });
  };

  const goToPrevSlide = () => {
    if (isTransitioning) {
      return;
    }

    setIsTransitioning(true);
    amplitudeEventButtonClick();
    setCurrentSlide((curr) => {
      const newSlide = curr - 1;
      return newSlide;
    });
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLElement>): void => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLElement>): void => {
    const endX = e.changedTouches[0]?.clientX ?? 0;

    if (Math.abs(touchStartX.current - endX) > 50) {
      if (touchStartX.current - endX > 0) {
        goToNextSlide();
      } else {
        goToPrevSlide();
      }
    }
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  return (
    <Box display="flex" alignItems="center">
      {controlVariant === 'sides' && (
        <Box mr={5} zIndex={1}>
          <ControlButton onClick={goToPrevSlide} size="small">
            <Icon24UtilityChevronSmallLeft />
          </ControlButton>
        </Box>
      )}
      <Box
        ref={containerRef}
        sx={{
          '--item-width': 'auto',
          position: 'relative',
          width: '100%',
        }}>
        <Box
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          sx={{
            display: 'flex',
            overflow: 'hidden',
            width: '100%',
          }}
          {...slotProps}>
          <Box
            sx={{
              display: 'flex',
              transform: `translateX(-${currentSlide * slideOffset}px)`,
              transition: isTransitioning ? 'transform 0.3s ease-in-out' : 'none',
            }}>
            {slides.map((slide, index) => (
              <Box
                key={`carousel-slide-${index + 1}`}
                aria-hidden={index < currentSlide || index > currentSlide + itemsPerView - 1}
                sx={{
                  width: 'var(--item-width)',
                  flexShrink: 0,
                  mr: index === slides.length - 1 ? 0 : itemGap,
                  '& img': {
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover',
                    display: 'block',
                    maxWidth: '100%',
                    aspectRatio: '3/2',
                  },
                  p: 1,
                }}>
                {slide}
              </Box>
            ))}
          </Box>
        </Box>
        {controlVariant === 'topRight' && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              position: 'absolute',
              top: '-85px',
              right: '0',
              width: '100%',
            }}>
            <ControlButton sx={{ mr: 2 }} onClick={goToPrevSlide} size="large">
              <Icon24ChevronLeft />
            </ControlButton>
            <ControlButton onClick={goToNextSlide} size="large">
              <Icon24ChevronRight />
            </ControlButton>
          </Box>
        )}
        {showProgressIndicator && (
          <Box sx={{ mt: { xs: 2, md: 6 }, '& ul .dot': { height: `5px` } }}>
            <ProgressDots stepNumber={(currentSlide % totalSteps) + 1} totalSteps={totalSteps} />
          </Box>
        )}
      </Box>
      {controlVariant === 'sides' && (
        <Box ml={5} zIndex={1}>
          <ControlButton onClick={goToNextSlide} size="small">
            <Icon24UtilityChevronSmallRight />
          </ControlButton>
        </Box>
      )}
    </Box>
  );
};
